// TopNavLayout.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import { Avatar, Badge, Dropdown } from "flowbite-react";
import { IoIosArrowDown, IoMdRefresh } from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import { useApplication } from "../contexts/ApplicationContext";
import { permissionChecker, removeToken } from "../utils/permissions";
import { calculateExpirationPeriodTest, formatToReal } from "../utils/utils";
import ModalAddCredit from "./modals/ModalAddCredit";

/**
 * Componente de layout com navegação no topo.
 * - Substitui a Sidebar inteira
 * - Mantém mesmas permissões/links
 * - Usa Flowbite + Tailwind + framer-motion
 */
export default function TopNavLayout() {
  const { user, userPermission, refetchUser } = useApplication();
  const location = useLocation();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [titlePage, setTitlePage] = useState("Dashboard");

  const daysLeft = calculateExpirationPeriodTest(
    user?.Company?.planExpirationDate
  );

  const logoSrc = "/assets/logo-white.png";

  // Seções de navegação (desktop agrupado)
  // Amplia Hub fica como item pai e clicável diretamente
  const menuSections = useMemo(
    () => [
      {
        id: "dashboard",
        type: "single",
        name: "Dashboard",
        icon: "equalizer",
        items: [
          {
            name: "Dashboard",
            link: "/dashboard",
            icon: "equalizer",
            permission: ["USER", "USER_CALENDARS"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        id: "ampliahub",
        type: "single",
        name: "Amplia Hub",
        icon: "assignment_ind",
        items: [
          {
            name: "Amplia Hub",
            link: "/crm",
            icon: "assignment_ind",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        name: "Tarefas",
        type: "single",
        link: "/crm/tasks",
        icon: "pending_actions",
        items: [
          {
            name: "Tarefas",
            link: "/crm/tasks",
            icon: "pending_actions",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        id: "operacoes",
        type: "group",
        name: "Operações",
        icon: "sync_alt",
        items: [
          {
            name: "Consultas",
            link: "/queries",
            icon: "manage_search",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
          {
            name: "Simulações",
            link: "/simulations",
            icon: "add_chart",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        id: "crm_clientes",
        type: "group",
        name: "Clientes",
        icon: "account_tree",
        items: [
          {
            name: "Clientes",
            link: "/admin/customers",
            icon: "corporate_fare",
            permission: ["ADMIN"],
            typeCompany: ["REPRESENTANTE", "ADMIN"],
          },
        ],
      },
      {
        id: "relatorios",
        type: "group",
        name: "Relatórios",
        icon: "grading",
        items: [
          {
            name: "Consultas",
            link: "/reports/queries",
            icon: "manage_search",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
          // novos relatórios podem ser adicionados aqui depois
        ],
      },
      {
        id: "admin",
        type: "group",
        name: "Administração",
        icon: "admin_panel_settings",
        items: [
          {
            name: "Usuários",
            link: "/users",
            icon: "group",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
    ],
    []
  );

  // Título da página (derivado da rota atual)
  useEffect(() => {
    const flat = [];
    menuSections.forEach((section) => {
      (section.items || []).forEach((item) => {
        flat.push({ name: item.name, link: item.link });
      });
    });

    const found = flat.find((f) => f.link === location.pathname);
    setTitlePage(found?.name || "Dashboard");
  }, [location.pathname, menuSections]);

  // Helpers
  const hasPermItem = (item) =>
    permissionChecker(item.permission, userPermission, user, item.typeCompany);

  // Seções visíveis (itens filtrados por permissão)
  const visibleSections = useMemo(
    () =>
      menuSections
        .map((section) => ({
          ...section,
          items: (section.items || []).filter(hasPermItem),
        }))
        .filter((section) => section.items.length > 0),
    [menuSections, userPermission, user]
  );

  const isActive = (href) => location.pathname === href;

  const isSectionActive = (section) =>
    section.items.some((item) => isActive(item.link));

  const toggleMobileGroup = (id) =>
    setExpandedMobile((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleRefresh = () => refetchUser();

  // Fechar o menu mobile ao navegar
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur-sm bg-black/75 border-b border-white/10">
        <div className="max-w-9xl mx-auto px-3 md:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            {/* Logo + (Mobile) Burger */}
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="flex items-center gap-2">
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="h-12 w-auto drop-shadow"
                />
              </Link>

              {/* Mobile burger */}
              <button
                className="inline-flex items-center justify-center rounded-md p-2 text-gray-300 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white md:hidden"
                onClick={() => setIsMobileOpen((o) => !o)}
                aria-label="Abrir menu"
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {/* Badge tipo de conta (desktop) */}
              {user?.Company?.companyTypeId === "REPRESENTANTE" && (
                <Badge className="hidden sm:inline-flex ml-2" color="blue">
                  Representante
                </Badge>
              )}
            </div>

            {/* Navegação desktop agrupada */}
            <nav className="hidden md:flex items-center gap-1">
              {visibleSections.map((section) => {
                const sectionActive = isSectionActive(section);

                // Seção "single" (Dashboard, Amplia Hub) -> link direto
                if (section.type === "single") {
                  const item = section.items[0];
                  return (
                    <TopLink
                      key={section.id}
                      to={item.link}
                      icon={section.icon}
                      active={sectionActive}
                      label={section.name}
                    />
                  );
                }

                // Demais seções -> dropdown
                return (
                  <TopDropdown
                    key={section.id}
                    label={section.name}
                    icon={section.icon}
                    items={section.items}
                    isActiveGroup={sectionActive}
                  />
                );
              })}
            </nav>

            {/* Ações à direita */}
            <div className="flex items-center gap-2">
              {/* Saldo e ações (somente PRE) */}
              {user?.Company?.typePayment === "PRE" && (
                <div className="hidden md:flex items-center gap-2">
                  <Badge className="bg-white/10 text-white border border-white/20">
                    Saldo: {formatToReal(user?.Company?.credit || 0)}
                  </Badge>
                  <button
                    className="rounded-md bg-primary text-white px-2 py-2 hover:brightness-110 transition"
                    onClick={() => setIsOpenModalAddCredit(true)}
                    title="Adicionar créditos"
                  >
                    <MdOutlinePayments size={18} />
                  </button>
                  <button
                    className="rounded-md bg-white/10 text-white px-2 py-2 hover:bg-white/20 transition"
                    onClick={handleRefresh}
                    title="Atualizar"
                  >
                    <IoMdRefresh size={18} />
                  </button>
                </div>
              )}

              {/* Usuário */}
              <Dropdown
                label={<Avatar rounded size="sm" img={user?.imageUrl} />}
                arrowIcon={false}
                inline
              >
                <Dropdown.Header>
                  <span className="block text-sm font-medium truncate">
                    {user?.email}
                  </span>
                </Dropdown.Header>

                <Dropdown.Item className="py-0 px-0">
                  <Link to="/profile" className="block w-full py-2 px-4">
                    Meu perfil
                  </Link>
                </Dropdown.Item>

                <Dropdown.Divider />

                <Dropdown.Item className="py-0 px-0">
                  <a
                    target="_blank"
                    href="https://wa.me/+5515996730404"
                    rel="noreferrer"
                    className="block w-full py-2 px-4"
                  >
                    Entre em contato
                  </a>
                </Dropdown.Item>

                <Dropdown.Item className="py-0 px-0">
                  <Link to="/lgpd" className="block w-full py-2 px-4">
                    Aviso LGPD
                  </Link>
                </Dropdown.Item>

                <Dropdown.Divider />

                <Dropdown.Item onClick={removeToken}>Sair</Dropdown.Item>
              </Dropdown>
            </div>
          </div>
        </div>
      </header>

      {/* Menu Mobile */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.aside
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed top-16 left-0 right-0 z-40 bg-black text-white border-b border-white/10"
          >
            {/* Saldo PRE no mobile */}
            {user?.Company?.typePayment === "PRE" && (
              <div className="px-4 py-3 flex items-center gap-2 border-b border-white/10">
                <Badge className="bg-white/10 text-white border border-white/20">
                  Saldo: {formatToReal(user?.Company?.credit || 0)}
                </Badge>
                <button
                  className="rounded-md bg-primary text-white px-2 py-2"
                  onClick={() => setIsOpenModalAddCredit(true)}
                  title="Adicionar créditos"
                >
                  <MdOutlinePayments size={18} />
                </button>
                <button
                  className="rounded-md bg-white/10 text-white px-2 py-2"
                  onClick={handleRefresh}
                  title="Atualizar"
                >
                  <IoMdRefresh size={18} />
                </button>
              </div>
            )}

            <div className="px-2 py-2">
              {visibleSections.map((section) => {
                const sectionActive = isSectionActive(section);

                // Seção com 1 item só -> link direto
                if (section.type === "single") {
                  const item = section.items[0];
                  return (
                    <MobileItem
                      key={section.id}
                      to={item.link}
                      icon={section.icon}
                      active={sectionActive}
                      label={section.name}
                    />
                  );
                }

                // Seções com múltiplos -> grupo expansível
                return (
                  <MobileGroup
                    key={section.id}
                    label={section.name}
                    icon={section.icon}
                    open={!!expandedMobile[section.id]}
                    onToggle={() => toggleMobileGroup(section.id)}
                    childrenItems={section.items}
                    isActiveGroup={sectionActive}
                  />
                );
              })}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Conteúdo (empurra abaixo do topo) */}
      <main className="max-w-9xl mx-auto px-3 md:px-6 lg:px-8 py-2">
        {/* Aqui entra o <Outlet /> ou children */}
        {/* Exemplo: <Outlet /> */}
      </main>

      {/* Modal de Créditos */}
      <ModalAddCredit
        isOpen={isOpenModalAddCredit}
        setOpenModal={setIsOpenModalAddCredit}
      />
    </>
  );
}

/* ===========================
 * Subcomponentes
 * =========================== */

function TopLink({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`px-3 h-10 rounded-md flex items-center gap-2 text-sm transition
        ${
          active
            ? "bg-white/15 text-white border border-white/20"
            : "text-white/80 hover:text-white hover:bg-white/10"
        }`}
    >
      {icon && (
        <Icon
          className={`text-base ${active ? "text-[#fff0e5]" : "text-white/80"}`}
        >
          {icon}
        </Icon>
      )}
      <span className="font-medium">{label}</span>
    </Link>
  );
}

function TopDropdown({ label, icon, items, isActiveGroup }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`px-3 h-10 rounded-md flex items-center gap-2 text-sm transition
          ${
            isActiveGroup
              ? "bg-white/15 text-white border border-white/20"
              : "text-white/80 hover:text-white hover:bg-white/10"
          }`}
      >
        {icon && (
          <Icon
            className={`text-base ${isActiveGroup ? "text-[#fff0e5]" : ""}`}
          >
            {icon}
          </Icon>
        )}
        <span className="font-medium">{label}</span>
        <IoIosArrowDown
          className={`text-white/70 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 mt-2 w-60 rounded-lg overflow-hidden border border-white/15 bg-black/95 backdrop-blur shadow-lg"
          >
            <div className="py-1">
              {items.map((child) => (
                <Link
                  key={child.link}
                  to={child.link}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10"
                >
                  {child.icon && (
                    <Icon className="text-[18px] text-white/70">
                      {child.icon}
                    </Icon>
                  )}
                  <span className="truncate">{child.name}</span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm mb-1
        ${
          active
            ? "bg-white/10 text-white border border-white/15"
            : "text-white/80 hover:text-white hover:bg-white/10"
        }`}
    >
      {icon && <Icon className="text-[18px] text-white/80">{icon}</Icon>}
      <span className="font-medium">{label}</span>
    </Link>
  );
}

function MobileGroup({
  label,
  icon,
  open,
  onToggle,
  childrenItems,
  isActiveGroup,
}) {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm
          ${
            isActiveGroup
              ? "bg-white/10 text-white border border-white/15"
              : "text-white/80 hover:text-white hover:bg-white/10"
          }`}
      >
        <span className="flex items-center gap-2">
          {icon && <Icon className="text-[18px] text-white/80">{icon}</Icon>}
          <span className="font-medium">{label}</span>
        </span>
        <IoIosArrowDown
          className={`text-white/70 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-2 mt-1"
          >
            {(childrenItems || []).map((child) => (
              <Link
                key={child.link}
                to={child.link}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/80 hover:text-white hover:bg:white/10 mb-1"
              >
                {child.icon && (
                  <Icon className="text-[16px] text-white/70">
                    {child.icon}
                  </Icon>
                )}
                <span>{child.name}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
